<template>
  <el-button
    link
    type="primary"
    @click="handleImport"
  >
    {{ proxy.$t('excel.Import') }}
  </el-button>
  <el-dialog
    v-model="upload.open"
    :title="proxy.$t('excel.Import')"
    width="500px"
    append-to-body
  >
    <el-upload
      v-model:file-list="fileList"
      :limit="1"
      accept=".xlsx, .xls"
      :headers="upload.headers"
      :disabled="upload.isUploading"
      :auto-upload="false"
      :on-change="handleFileChange"
      :on-remove="handleFileRemove"
      drag
      action="#"
    >
      <!-- 注意：action="#" 是为了防止报错，实际走手动逻辑 -->
      <el-icon class="el-icon--upload">
        <UploadFilled />
      </el-icon>
      <div class="el-upload__text">
        {{ proxy.$t('excel.cilckPushTip') }}<em style="color: var(--color-1)">{{ proxy.$t('excel.cilckPush') }}</em>
      </div>

      <template #tip>
        <div class="text-center el-upload__tip">
          <!-- <div class="el-upload__tip"><el-checkbox v-model="upload.updateSupport" />是否更新已经存在的用户数据</div> -->
          {{ proxy.$t('excel.typew') }}
        </div>
      </template>
    </el-upload>

    <template #footer>
      <div class="dialog-footer">
        <!-- 添加 loading 状态 -->
        <el-button
          type="primary"
          :loading="upload.isUploading"
          @click="submitFileForm"
        >
          {{ proxy.$t('excel.checkTemplate') }}
        </el-button>
        <el-button @click="upload.open = false">
          {{ proxy.$t('Shared.Cancel') }}
        </el-button>
      </div>
    </template>
  </el-dialog>
  <el-dialog
    v-model="preOpen"
    :title="proxy.$t('excel.Import')"
    width="450px"
    append-to-body
  >
    <div class="PreCenter">
      <el-icon
        v-if="successCount > 0 && failCount > 0"
        :size="24"
        color="#ffa200"
      >
        <WarningFilled />
      </el-icon>

      <el-icon
        v-else-if="failCount > 0"
        :size="24"
        color="#e1291e"
      >
        <CircleCloseFilled />
      </el-icon>
      <el-icon
        v-else
        :size="26"
        color="#00c261"
      >
        <SuccessFilled />
      </el-icon>

      <div class="pretip">
        {{ successCount > 0 && failCount > 0 ? proxy.$t('excel.sFail') : successCount > 0 ? proxy.$t('excel.success') : proxy.$t('excel.fail') }}
      </div>
      <div class="perfill">
        <span class="peritem">{{ proxy.$t('excel.success') }}:<span class="successCount">{{ successCount }}</span></span>
        <span
          v-show="failCount > 0"
          class="peritem"
        >{{ proxy.$t('excel.fail') }}:<span class="failCount">{{ failCount }}</span></span>
        <span
          v-show="failCount > 0"
          class="peritem active"
          @click="failDetail"
        >{{ proxy.$t('excel.failDetail') }}</span>
        <span
          v-show="failCount == 0 && successCount > 0"
          class="peritem"
        >{{ proxy.$t('excel.successtip') }}</span>
      </div>
      <div
        v-show="failCount > 0"
        class="perfill"
        style="margin-top: 10px"
      >
        {{ proxy.$t('excel.tip') }}
      </div>
    </div>
    <template #footer>
      <div class="dialog-footer">
        <!-- 添加 loading 状态 -->
        <el-button
          link
          @click="preOpen = false"
        >
          {{ proxy.$t('Shared.Cancel') }}
        </el-button>
        <el-button
          v-show="failCount == 0"
          type="primary"
          link
          :loading="upload.isUploading"
          @click="submitFile"
        >
          {{
            proxy.$t('excel.Import')
          }}
        </el-button>
        <el-button
          v-if="successCount > 0 && failCount > 0"
          type="primary"
          :loading="upload.isUploading"
          link
          @click="submitFile"
        >
          {{ proxy.$t('excel.failImport') }}
        </el-button>
      </div>
    </template>
    <el-drawer
      v-model="drawer"
      :with-header="false"
    >
      <div class="title">
        {{ proxy.$t('excel.errorTitle') }} ({{ errorList.length }}) <el-icon
          class="icon"
          @click="drawer = false"
        >
          <Close />
        </el-icon>
      </div>

      <div class="errorList">
        <div
          v-for="(item, index) in errorList"
          :key="`${item.name || 'error'}-${index}`"
          class="item"
        >
          <span class="li" />
          {{ item.name }}:{{ item.failReason }}
        </div>
      </div>
    </el-drawer>
  </el-dialog>
</template>
<script setup lang="ts">
import { CircleCloseFilled, Close, SuccessFilled, UploadFilled, WarningFilled } from '@element-plus/icons-vue';
import { getCurrentInstance, reactive, ref, watch } from 'vue';
import { prepareEmployeeImport, type EmployeeImportType } from '@/utils/form/employeeImport';
import { ElMessage, type UploadUserFile } from 'element-plus';
import request, { globalHeaders } from '../../utils/request.ts';

const { proxy } = getCurrentInstance();
const fileList = ref<UploadUserFile[]>([]);
const currentFile = ref<File | null>(null);
const preparedFile = ref<File | null>(null);
let fileRevision = 0; // 用于暂存选择的文件
const successCount = ref(0);
const failCount = ref(0);
const errorList = ref([]);
const drawer = ref(false);

const props = defineProps({
  api: { type: String, default: '' },
  preApi: { type: String, default: '' },
  employeeType: { type: String, required: true, validator: (value: string) => ['brand', 'shop'].includes(value) },
});
const preOpen = ref(false);

const upload = reactive({
  open: false,
  title: '用户导入',
  isUploading: false,
  updateSupport: false,
  headers: globalHeaders(),
  baseUrl: import.meta.env.VITE_APP_BASE_API,
});

const t = (key: string, params?: Record<string, string | number>) => proxy.$t(key, params);
const resetPrepared = () => {
  fileRevision++;
  preparedFile.value = null;
  preOpen.value = false;
  successCount.value = 0;
  failCount.value = 0;
  errorList.value = [];
  drawer.value = false;
};
const handleFileChange = (file) => { resetPrepared(); currentFile.value = file.raw; };
const handleFileRemove = () => { resetPrepared(); currentFile.value = null; fileList.value = []; };
const failDetail = () => { drawer.value = true; };
watch(() => upload.open, open => { if (!open) handleFileRemove(); });
const handleImport = () => {
  if (upload.isUploading) return;
  handleFileRemove();
  upload.open = true;
};

async function submitFileForm() {
  if (upload.isUploading) return;
  if (!currentFile.value) return ElMessage.error(t('excel.chooseFile'));
  resetPrepared();
  const revision = fileRevision;
  const source = currentFile.value;
  upload.isUploading = true;
  try {
    const prepared = await prepareEmployeeImport(source, props.employeeType as EmployeeImportType, t);
    if (revision !== fileRevision) return;
    if (prepared.errors.length) {
      errorList.value = prepared.errors;
      failCount.value = new Set(prepared.errors.map(error => error.row ?? error.name)).size;
      preOpen.value = true;
      return;
    }
    const formData = new FormData();
    formData.append('file', prepared.file);
    const checkRes = await request({ url: props.preApi, method: 'post', data: formData, headers: { 'Content-Type': 'multipart/form-data' } });
    if (revision !== fileRevision) return;
    if (checkRes.code === 200) {
      preparedFile.value = prepared.file;
      failCount.value = checkRes.data.failCount;
      successCount.value = checkRes.data.successCount;
      errorList.value = checkRes.data.errorList || [];
      preOpen.value = true;
    }
  } catch {
    // 公共请求封装提示失败；保留用户选择的原文件供重试。
  } finally {
    upload.isUploading = false;
  }
}
async function submitFile() {
  if (upload.isUploading || !preparedFile.value || !successCount.value) return;
  const revision = fileRevision;
  const formData = new FormData();
  // 与预检完全相同的文件，避免规范化、换文件或重试造成两次内容不同。
  formData.append('file', preparedFile.value);
  upload.isUploading = true;
  try {
    const importRes = await request({ url: props.api, method: 'post', data: formData, headers: { 'Content-Type': 'multipart/form-data' } });
    if (revision !== fileRevision) return;
    if (importRes.code === 200) {
      ElMessage.success(t('excel.importSuccess'));
      upload.open = false;
      preOpen.value = false;
      proxy.$emit('callback');
    }
  } catch {
    // 公共请求封装提示失败；保留已预检的副本。
  } finally {
    upload.isUploading = false;
  }
}
</script>
<style lang="scss" scoped>
.PreCenter {
  text-align: center;
  .pretip {
    color: #333;
    font-size: 16px;
    margin: 10px 0;
  }
  .perfill {
    font-size: 12px;
    color: #afadad;
    .peritem {
      margin-inline-end: 10px;
      &.active {
        color: var(--color-1);
        cursor: pointer;
      }
      .successCount {
        color: #00c261;
      }
      .failCount {
        color: #e1291e;
      }
    }
  }
}
.title {
  height: 30px;
  border-bottom: 1px #eeeeee solid;
  font-weight: 600;
  position: relative;
  .icon {
    position: absolute;
    inset-inline-end: 10px;
    font-size: 20px;
  }
}
.errorList {
  height: calc(100vh - 66px);
  overflow-y: auto;
  padding: 10px 0;
  .item {
    margin-bottom: 10px;

    .li {
      display: inline-block;
      width: 4px;
      height: 4px;
      background: rgb(185, 183, 183);
      border-radius: 2px;
    }
  }
}
</style>
